import React from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import btnClose from '../../images/common/close.svg';
import {
  Main,
  Contents,
  BaseDialog,
  CloseButton,
  CloseButtonImage,
} from '../../styles/common/Dialog';
import {
  DialogHeader,
  ButtonsContainer,
  MessageContainer,
} from './styles/ConfirmationDialogStyles';
import AppButton from '../../styles/common/AppButton';
import ColorSF2 from '../../styles/common/ColorSF2';

/**
 * 操作を確認するためのダイアログボックス
 *
 * @param {Object} props
 * @param {React.ReactNode} props.headerText - ヘッダーに表示するテキスト。
 * @param {React.ReactNode} props.messageText - 本文に表示する確認のメッセージ。
 * @param {Function} props.onCancel キャンセルボタン押下時のコールバック関数
 * @param {Function} props.onConfirm 確認ボタン押下時のコールバック関数
 * @param {React.ReactNode} [props.cancelButtonText] - キャンセルボタンに表示するテキスト。
 * @param {React.ReactNode} [props.confirmButtonText] - 確認ボタンに表示するテキスト。
 * @param {string} [props.confirmButtonColor] - 確認ボタンの背景色。
 * @example
 *   <ConfirmationDialog
 *     headerText={<FormattedMessage id="confirmDialog.header" />}
 *     messageText={<FormattedMessage id="confirmDialog.message" />}
 *     onCancel={() => {}}
 *     onConfirm={() => {}}
 *   />
 */
const ConfirmationDialog = ({
  headerText,
  messageText,
  onCancel,
  onConfirm,
  onSecondConfirm,
  cancelButtonText,
  confirmButtonText,
  secondConfirmButtonText,
  confirmButtonColor,
  secondConfirmButtonColor,
}) => (
  <Main>
    <BaseDialog>
      <Contents>
        <DialogHeader>
          {headerText}
          {onCancel && (
            <CloseButton
              id="closeButton"
              role="presentation"
              className="button"
              onClick={() => onCancel()}
            >
              <CloseButtonImage src={btnClose} alt="close" />
            </CloseButton>
          )}
        </DialogHeader>
        <MessageContainer>{messageText}</MessageContainer>
        <ButtonsContainer>
          {secondConfirmButtonText && onSecondConfirm && (
            <AppButton
              id="secondConfirmButton"
              role="presentation"
              onClick={onSecondConfirm}
              large
              bgColor={secondConfirmButtonColor || confirmButtonColor}
              bold
            >
              {secondConfirmButtonText}
            </AppButton>
          )}
          {onCancel && (
            <AppButton
              id="cancelButton"
              role="presentation"
              onClick={onCancel}
              large
              bgColor={ColorSF2.white}
              color={ColorSF2.black}
              borderColor={ColorSF2.mediumGray}
              bold
            >
              {cancelButtonText || (
                <FormattedMessage id="userPrompt.button.cancel" />
              )}
            </AppButton>
          )}
          <AppButton
            id="confirmButton"
            role="presentation"
            onClick={onConfirm}
            large
            bgColor={confirmButtonColor}
            bold
          >
            {confirmButtonText || (
              <FormattedMessage id="userPrompt.button.delete" />
            )}
          </AppButton>
        </ButtonsContainer>
      </Contents>
    </BaseDialog>
  </Main>
);

ConfirmationDialog.propTypes = {
  headerText: PropTypes.node.isRequired,
  messageText: PropTypes.node.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  secondConfirmButtonText: PropTypes.node,
  onSecondConfirm: PropTypes.func,
  secondConfirmButtonColor: PropTypes.string,
  cancelButtonText: PropTypes.node,
  confirmButtonText: PropTypes.node,
  confirmButtonColor: PropTypes.string,
};

ConfirmationDialog.defaultProps = {
  cancelButtonText: '',
  confirmButtonText: '',
  secondConfirmButtonText: null,
  onSecondConfirm: null,
  secondConfirmButtonColor: null,
  confirmButtonColor: ColorSF2.red,
};

export default ConfirmationDialog;
